// ControlPanel.tsx - Updated to use cloud function proxy with Organization Location Fix

import React, { useState, useEffect } from 'react';
import { 
  Firestore,
  collection,
  addDoc,
  doc,
  getDoc,
  query,
  where,
  getDocs,
  updateDoc
} from 'firebase/firestore';
import { Functions } from 'firebase/functions';
import { useAuth } from '../../../hooks/AuthProvider';
import { useLocations } from '../../../hooks/useLocations'; // Add this import
import { App, Theme } from './AppManager';
import { generateHTMLFile } from './generator/generateHTML';
import { generateManifestFile } from './generator/generateManifest';
import { downloadPublicFiles } from './generator/generatePublicZip';
import { createImageProcessor } from './generator/imageProcessor';
import ThemeUpdateRequest from './ThemeUpdateRequest';
import { LOCATION_OPTIONS, getLocationById } from './config/locationConfig';
import LoadingPage from '../../LoadingPage';

// Default environment variables
const DEFAULT_ENV_VARS = {
  STORE_CONFIG_CACHE_MAX_FIELDS: "100",
  CANCEL_ORDER_ENDPOINT_URL: "https://europe-west2-dine-central.cloudfunctions.net/cancelOrder",
  MAILING_LIST_BATCH_SIZE: "500",
  ORDER_BATCH_SIZE: "10"
};

interface ControlPanelProps {
  db: Firestore;
  functions: Functions;
  appData: App | null;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
  onBack: () => void;
  onViewWorkflow: () => void;
  onViewStores: () => void;
  canApproveDeployment: boolean;
  selectedOrganizationId: string;
  onBackToOrganizations: () => void;
  onViewAdminPanel?: () => void;
  onViewPermissions?: () => void;
}

interface ImageGenerationStatus {
  status: 'checking' | 'cloud-function-ready' | 'cors-fallback' | 'failed' | 'no-icon';
  message: string;
  method?: string;
  canGenerate: boolean;
}

const ControlPanel: React.FC<ControlPanelProps> = ({
  db,
  functions,
  appData,
  onError,
  onSuccess,
  setLoading,
  onBack,
  onViewWorkflow,
  onViewStores,
  canApproveDeployment,
  selectedOrganizationId,
  onBackToOrganizations,
  onViewAdminPanel,
  onViewPermissions
}) => {
  const { currentUser } = useAuth();
  
  // Add the locations hook
  const { getOrganizationById } = useLocations();
  
  const [showThemeUpdateForm, setShowThemeUpdateForm] = useState(false);
  const [stores, setStores] = useState<any[]>([]);
  const [storesLoading, setStoresLoading] = useState(true);
  const [imageStatus, setImageStatus] = useState<ImageGenerationStatus>({
    status: 'checking',
    message: 'Checking image generation capability...',
    canGenerate: false
  });

  // Helper function to get the correct location ID
  const getCorrectLocationId = (): string => {
    console.log("selected Organization ID: ", selectedOrganizationId)
    if (selectedOrganizationId) {
      const orgLocation = getOrganizationById(selectedOrganizationId);
      if (orgLocation) {
        console.log('ControlPanel using organization location:', orgLocation.id, 'for org:', selectedOrganizationId);
        return orgLocation.id;
      }
    }
    
    if (appData?.locationId) {
      console.log('ControlPanel using app locationId:', appData.locationId);
      return appData.locationId;
    }
    
    throw Error("Location ID problem")
  };

  // Load stores for preview - must be before early return
  useEffect(() => {
    const loadStores = async () => {
      if (!appData?.id) {
        setStoresLoading(false);
        return;
      }

      setStoresLoading(true);
      try {
        const q = query(
          collection(db, 'stores'),
          where('appId', '==', appData.id)
        );
        const querySnapshot = await getDocs(q);
        
        const storesList: any[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          storesList.push({
            ...data,
            id: doc.id
          });
        });

        setStores(storesList);
      } catch (err) {
        console.error('Error loading stores:', err);
      } finally {
        setStoresLoading(false);
      }
    };

    loadStores();
  }, [db, appData?.id]);

  // Test image generation capability on mount and when app icon changes
  useEffect(() => {
    const testImageGeneration = async () => {
      if (!appData?.appIcon) {
        setImageStatus({
          status: 'no-icon',
          message: 'No app icon configured. Upload an icon to enable image generation.',
          canGenerate: false
        });
        return;
      }

      setImageStatus({
        status: 'checking',
        message: 'Testing image generation capability...',
        canGenerate: false
      });

      try {
        const imageProcessor = createImageProcessor(functions);
        const testResult = await imageProcessor.testImageProcessing(appData.appIcon, appData.id);
        
        if (testResult.success) {
          if (testResult.method === 'cloud-function') {
            setImageStatus({
              status: 'cloud-function-ready',
              message: '✅ Cloud function proxy ready - images will generate automatically',
              method: 'cloud-function',
              canGenerate: true
            });
          } else if (testResult.method === 'direct-cors') {
            setImageStatus({
              status: 'cors-fallback',
              message: '✅ Direct access available - images will generate automatically',
              method: 'direct-cors',
              canGenerate: true
            });
          } else {
            setImageStatus({
              status: 'cloud-function-ready',
              message: '✅ Basic processing ready',
              method: testResult.method,
              canGenerate: true
            });
          }
        } else {
          setImageStatus({
            status: 'failed',
            message: `❌ Image generation failed: ${testResult.error || 'Unknown error'}`,
            canGenerate: false
          });
        }
      } catch (error) {
        console.error('Error testing image generation:', error);
        setImageStatus({
          status: 'failed',
          message: `❌ Test failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
          canGenerate: false
        });
      }
    };

    testImageGeneration();
  }, [appData?.appIcon, appData?.id, functions]);

  // Debug logging
  useEffect(() => {
    console.log('ControlPanel Debug Info:', {
      selectedOrganizationId,
      appDataLocationId: appData?.locationId,
      appDataOrganizationId: appData?.organizationId,
      correctLocationId: getCorrectLocationId()
    });
  }, [selectedOrganizationId, appData?.locationId, appData?.organizationId]);

  // Early return after all hooks
  if (!appData) return null;

  // Generate React .env file content - FIXED
  const generateReactEnvFileContent = () => {
    if (!appData.firebaseConfig) return '';
  
    // Use the helper function to get correct location
    const location = getLocationById(getCorrectLocationId());
  
    const envLines = [
      `REACT_APP_FIREBASE_apiKey=${appData.firebaseConfig.apiKey}`,
      `REACT_APP_FIREBASE_authDomain=${appData.firebaseConfig.authDomain}`,
      `REACT_APP_FIREBASE_projectId=${appData.firebaseConfig.projectId}`,
      `REACT_APP_FIREBASE_storageBucket=${appData.firebaseConfig.storageBucket}`,
      `REACT_APP_FIREBASE_messagingSenderId=${appData.firebaseConfig.messagingSenderId}`,
      `REACT_APP_FIREBASE_appId=${appData.firebaseConfig.appId}`,
    ];
  
    if (appData.firebaseConfig.measurementId) {
      envLines.push(`REACT_APP_FIREBASE_measurementId=${appData.firebaseConfig.measurementId}`);
    }
  
    if (appData.theme) {
      envLines.push(
        `REACT_APP_THEME=${appData.theme.variant}`,
        `REACT_APP_THEME_OVERRIDE_ACCENT_COLOR=${appData.theme.accentColor}`
      );
    }
  
    envLines.push(
      `REACT_APP_COUNTRY_CODE=${location.countryCode}`,
      `REACT_APP_CURRENCY_CODE=${location.currencyCode.toLowerCase()}`,
      `REACT_APP_CURRENCY_SYMBOL=${location.currencySymbol.replace('$', '\\$')}`, // ← ESCAPE THE $      
      `REACT_APP_STRIPE_TEST_KEY=${location.stripeTestKey}`,
      `REACT_APP_STRIPE_LIVE_KEY=${location.stripeLiveKey}`,
      `REACT_APP_REGION=${appData.region}`,
    );
  
    return envLines.join('\n');
  };

  // Generate Cloud Functions .env file content - FIXED
  const generateCloudFunctionsEnvFileContent = async (accessToken: string = '') => {
    // Use the helper function to get correct location
    const location = getLocationById(getCorrectLocationId());
  
    const envContent = [
      `STORE_CONFIG_CACHE_MAX_FIELDS=${DEFAULT_ENV_VARS.STORE_CONFIG_CACHE_MAX_FIELDS}`,
      `CANCEL_ORDER_ENDPOINT_URL=${DEFAULT_ENV_VARS.CANCEL_ORDER_ENDPOINT_URL}`,
      `MAILING_LIST_BATCH_SIZE=${DEFAULT_ENV_VARS.MAILING_LIST_BATCH_SIZE}`,
      `ORDER_BATCH_SIZE=${DEFAULT_ENV_VARS.ORDER_BATCH_SIZE}`,
      `MERCHANT_ACCESSTOKEN=${accessToken}`,
      `APP_NAME=${appData.name}`,
      `CURRENCY_CODE=${location.currencyCode}`,
      `CURRENCY_SYMBOL=${location.currencySymbol.replace('$', '\\$')}`, // ← ESCAPE THE $      
      `REGION=${appData.region}`,
      `ORGANIZATION=${location.organizationName}`
    ].join('\n');
  
    return envContent;
  };

  // Generate and download React .env file
  const handleGenerateReactEnvFile = async () => {
    if (!appData.firebaseConfig) {
      onError('Firebase configuration is missing.');
      return;
    }

    try {
      const fileContent = generateReactEnvFileContent();
      const fileName = `.env.react.${appData.id}`;
      
      const blob = new Blob([fileContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      
      onSuccess(`React environment file ${fileName} has been generated.`);
    } catch (err: any) {
      console.error('Error generating React environment file:', err);
      onError(err.message || 'Failed to generate React environment file.');
    }
  };

  // Generate and download Cloud Functions .env file
  const handleGenerateCloudFunctionsEnvFile = async () => {
    setLoading(true);
    try {
      const appSecurityRef = doc(db, 'appSecurity', appData.id);
      const appSecurityDoc = await getDoc(appSecurityRef);
      
      let accessToken = '';
      if (appSecurityDoc.exists()) {
        const securityData = appSecurityDoc.data();
        accessToken = securityData.accessToken || '';
      }
      
      const fileContent = await generateCloudFunctionsEnvFileContent(accessToken);
      const fileName = `.env.functions.${appData.id}`;
      
      const blob = new Blob([fileContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      
      onSuccess(`Cloud Functions environment file ${fileName} has been generated.`);
    } catch (err: any) {
      console.error('Error generating Cloud Functions environment file:', err);
      onError(err.message || 'Failed to generate Cloud Functions environment file.');
    } finally {
      setLoading(false);
    }
  };
  // Handle download public files with cloud function support
  const handleDownloadPublicFiles = async () => {
    if (!appData) {
      onError('App data is missing.');
      return;
    }
  
    try {
      // Get the custom domain if configured
      let customDomain = '';
      if (appData.domains) {
        const domainEntries = Object.keys(appData.domains);
        const userDomain = domainEntries.find(domain => !domain.includes('.web.app'));
        customDomain = userDomain || '';
      }
  
      // Download with cloud function support
      console.log('🚀 Starting public files download with cloud function support');
      await downloadPublicFiles({
        appData,
        customDomain,
        functions // Pass functions for cloud function proxy
      });
      
      // Provide appropriate success message based on generation status
      let successMessage = `Public folder files for ${appData.name} have been downloaded`;
      
      switch (imageStatus.status) {
        case 'cloud-function-ready':
          successMessage += ' with app icons generated via cloud function proxy.';
          break;
        case 'cors-fallback':
          successMessage += ' with app icons generated directly.';
          break;
        case 'no-icon':
          successMessage += '. No app icon configured - upload an icon to generate favicon files.';
          break;
        case 'failed':
          successMessage += '. Image generation failed - manual instructions included.';
          break;
        default:
          successMessage += '. Check the README file for image generation status.';
      }
      
      onSuccess(successMessage);
    } catch (err: any) {
      console.error('Error downloading public files:', err);
      onError(err.message || 'Failed to download public files.');
    }
  };

  // Handle theme update request submission
  const handleThemeUpdateRequest = async (themeData: Theme) => {
    if (!currentUser?.email) {
      onError('User email not available.');
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        appId: appData.id,
        appName: appData.name,
        requestedBy: currentUser.email,
        requestedAt: new Date(),
        themeData: themeData,
        status: 'pending'
      };

      await addDoc(collection(db, 'themeUpdateRequests'), requestData);
      
      onSuccess('Theme update request submitted successfully. An admin will review it shortly.');
      setShowThemeUpdateForm(false);
    } catch (err: any) {
      console.error('Error submitting theme update request:', err);
      onError(err.message || 'Failed to submit theme update request.');
    } finally {
      setLoading(false);
    }
  };

  // Get icon status display text and color
  const getIconStatusDisplay = () => {
    if (!appData.appIcon) {
      return { text: '✗ Not configured', className: 'controlpanel-not-configured' };
    }
    
    switch (imageStatus.status) {
      case 'cloud-function-ready':
        return { text: '✓ Cloud function ready', className: 'controlpanel-cloud-function-ready' };
      case 'cors-fallback':
        return { text: '✓ Direct access ready', className: 'controlpanel-cors-ready' };
      case 'checking':
        return { text: '⏳ Checking...', className: 'controlpanel-checking' };
      case 'failed':
        return { text: '❌ Generation failed', className: 'controlpanel-failed' };
      case 'no-icon':
        return { text: '✗ Not configured', className: 'controlpanel-not-configured' };
      default:
        return { text: '✓ Configured', className: 'controlpanel-configured' };
    }
  };

  const iconStatus = getIconStatusDisplay();

  return (
    <>
      <div className="applist-content-header">
        <h1 className="applist-page-title">Control Panel: {appData.name}</h1>
      </div>

      <div className="applist-content-body">
        <div className="controlpanel-grid">
          {/* My Stores Card */}
          <div className={`controlpanel-card controlpanel-stores-card ${stores.length === 0 ? 'controlpanel-incomplete' : ''}`}>
            <div className="controlpanel-card-header">
              <div className="controlpanel-card-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9,22 9,12 15,12 15,22"/>
                </svg>
              </div>
              <h4>My Stores</h4>
              {!storesLoading && (
                <span className={`controlpanel-store-count-badge ${stores.length === 0 ? 'controlpanel-empty' : 'controlpanel-has-stores'}`}>
                  {stores.length}
                </span>
              )}
            </div>
            <div className="controlpanel-card-content">
              {storesLoading ? (
                <LoadingPage />
              ) : stores.length === 0 ? (
                <div className="controlpanel-no-stores-simple">
                  <span className="controlpanel-warning-icon">⚠️</span>
                  <p>No stores added yet. Your app needs at least one store to accept orders.</p>
                </div>
              ) : (
                <div className="controlpanel-stores-preview">
                  {stores.slice(0, 3).map((store) => (
                    <div key={store.id} className="controlpanel-store-preview-item">
                      <div className="controlpanel-store-preview-info">
                        <h6>{store.name}</h6>
                        <p>{store.city}, {store.country}</p>
                      </div>
                      <div className="controlpanel-store-preview-status">
                        <span className={`controlpanel-status-dot ${store.stripeConnected ? 'controlpanel-connected' : 'controlpanel-pending'}`}></span>
                        <span className="controlpanel-status-text">
                          {store.stripeConnected ? 'Active' : 'Setup Pending'}
                        </span>
                      </div>
                    </div>
                  ))}
                  {stores.length > 3 && (
                    <div className="controlpanel-more-stores-indicator">
                      +{stores.length - 3} more store{stores.length - 3 > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              )}
              <div className="controlpanel-stores-actions">
                <button 
                  type="button"
                  className={`controlpanel-manage-stores-btn ${stores.length === 0 ? 'controlpanel-primary' : 'controlpanel-secondary'}`}
                  onClick={onViewStores}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {stores.length === 0 ? (
                      <>
                        <line x1="12" y1="5" x2="12" y2="19"/>
                        <line x1="5" y1="12" x2="19" y2="12"/>
                      </>
                    ) : (
                      <>
                        <path d="M9 12l2 2 4-4"/>
                        <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"/>
                        <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3"/>
                        <path d="M13 12h3a2 2 0 0 1 2 2v1"/>
                        <path d="M11 12H8a2 2 0 0 0-2 2v1"/>
                      </>
                    )}
                  </svg>
                  {stores.length === 0 ? 'Add Your First Store' : 'Manage Stores'}
                </button>
              </div>
            </div>
          </div>

          {/* App Information Card */}
          <div className="controlpanel-card">
            <div className="controlpanel-card-header">
              <div className="controlpanel-card-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
              </div>
              <h4>App Information</h4>
            </div>
            <div className="controlpanel-card-content">
              <div className="controlpanel-info-grid">
                <div className="controlpanel-info-item">
                  <label>App ID:</label>
                  <span>{appData.id}</span>
                </div>
                <div className="controlpanel-info-item">
                  <label>Name:</label>
                  <span>{appData.name}</span>
                </div>
                <div className="controlpanel-info-item">
                  <label>Type:</label>
                  <span>{appData.type}</span>
                </div>
                <div className="controlpanel-info-item">
                  <label>Region:</label>
                  <span>{appData.region}</span>
                </div>
                <div className="controlpanel-info-item">
                  <label>Location:</label>
                  <span>
                    {LOCATION_OPTIONS.find(loc => loc.id === appData.locationId)?.locationName || 'Unknown'}
                  </span>
                </div>
                <div className="controlpanel-info-item">
                  <label>Status:</label>
                  <span className="controlpanel-status-badge controlpanel-status-completed">Completed</span>
                </div>
                <div className="controlpanel-info-item">
                  <label>App Icon:</label>
                  <span className={`controlpanel-icon-status ${iconStatus.className}`}>
                    {iconStatus.text}
                  </span>
                </div>
                <div className="controlpanel-info-item">
                  <label>Image Generation:</label>
                  <span className={`controlpanel-image-generation-status ${imageStatus.canGenerate ? 'controlpanel-ready' : 'controlpanel-not-ready'}`}>
                    {imageStatus.message}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Theme Configuration Card */}
          <div className="controlpanel-card">
            <div className="controlpanel-card-header">
              <div className="controlpanel-card-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
              <h4>Theme Configuration</h4>
            </div>
            <div className="controlpanel-card-content">
              <div className="controlpanel-theme-preview">
                <div className="controlpanel-theme-info">
                  {appData.theme?.custom ? (
                    <div className="controlpanel-custom-theme-indicator">
                      <label>Custom Theme:</label>
                      <span className="controlpanel-custom-badge">Active</span>
                    </div>
                  ) : (
                    <>
                      <div className="controlpanel-theme-variant">
                        <label>Theme:</label>
                        <span>{appData.theme?.variant === 'modern-light' ? 'Modern Light' : 'Modern Dark'}</span>
                      </div>
                      <div className="controlpanel-theme-color">
                        <label>Accent Color:</label>
                        <div className="controlpanel-color-display">
                          <div 
                            className="controlpanel-color-swatch"
                            style={{ backgroundColor: appData.theme?.accentColor }}
                          ></div>
                          <span>{appData.theme?.accentColor}</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div className="controlpanel-theme-actions">
                  <button 
                    type="button"
                    className="controlpanel-request-theme-update-btn controlpanel-manage-stores-btn"
                    onClick={() => setShowThemeUpdateForm(true)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                    Request Theme Update
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Download Configuration Files - Only show if user can approve deployments */}
          {canApproveDeployment && (
            <div className="controlpanel-card">
              <div className="controlpanel-card-header">
                <div className="controlpanel-card-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                </div>
                <h4>Download Configuration Files</h4>
              </div>
              <div className="controlpanel-card-content">
                <div className="controlpanel-download-options">
                  <div className="controlpanel-file-group">
                    <h5>Environment Files</h5>
                    <div className="controlpanel-file-buttons">
                      <button 
                        type="button"
                        className="controlpanel-download-btn controlpanel-primary-download"
                        onClick={handleGenerateReactEnvFile}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="7 10 12 15 17 10"/>
                          <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        React .env File
                      </button>
                      
                      <button 
                        type="button"
                        className="controlpanel-download-btn controlpanel-primary-download"
                        onClick={handleGenerateCloudFunctionsEnvFile}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="7 10 12 15 17 10"/>
                          <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        Functions .env File
                      </button>
                    </div>
                  </div>

                  <div className="controlpanel-file-group">
                    <h5>Complete Public Folder</h5>
                    <div className="controlpanel-public-status">
                      <div className="controlpanel-status-indicator">
                        <span className={`controlpanel-status-icon ${imageStatus.canGenerate ? 'controlpanel-success' : 'controlpanel-warning'}`}>
                          {imageStatus.canGenerate ? '✅' : '⚠️'}
                        </span>
                        <span className="controlpanel-status-text">
                          {imageStatus.message}
                        </span>
                      </div>
                    </div>
                    <div className="controlpanel-file-buttons">
                      <button 
                        type="button"
                        className="controlpanel-download-btn controlpanel-primary-download"
                        onClick={handleDownloadPublicFiles}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="7 10 12 15 17 10"/>
                          <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        Download Public Folder Files
                      </button>
                    </div>
                    <small className="controlpanel-file-group-description">
                      Downloads HTML, manifest.json, robots.txt, sitemap.xml, and README with instructions.
                      {imageStatus.status === 'cloud-function-ready' 
                        ? ' App icons generated automatically via cloud function proxy.'
                        : imageStatus.status === 'cors-fallback'
                          ? ' App icons generated automatically via direct access.'
                          : imageStatus.status === 'no-icon'
                            ? ' No app icon configured - upload an icon to generate favicon files.'
                            : imageStatus.status === 'failed'
                              ? ' Manual image generation instructions included.'
                              : ' Check README for image generation status.'}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* App Management - Only show if user can approve deployments */}
          {canApproveDeployment && (
            <div className="controlpanel-card">
              <div className="controlpanel-card-header">
                <div className="controlpanel-card-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 12l2 2 4-4"/>
                    <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"/>
                    <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3"/>
                    <path d="M13 12h3a2 2 0 0 1 2 2v1"/>
                    <path d="M11 12H8a2 2 0 0 0-2 2v1"/>
                  </svg>
                </div>
                <h4>App Management</h4>
              </div>
              <div className="controlpanel-card-content">
                <div className="controlpanel-management-options">
                  <button 
                    type="button"
                    className="controlpanel-management-btn controlpanel-view-workflow-btn"
                    onClick={onViewWorkflow}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    View Deployment Workflow
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showThemeUpdateForm && (
        <ThemeUpdateRequest
          onSubmit={handleThemeUpdateRequest}
          onCancel={() => setShowThemeUpdateForm(false)}
          currentTheme={appData.theme}
        />
      )}
    </>
  );
};

export default ControlPanel;